// The client portal: its own sign-in, invite acceptance and a home page with
// invoices and payment history. Mounted at /portal/* (eloNest only) and at
// /p/:slug/* (the business's own branded link).
import React, { createContext, useContext, useEffect, useState } from 'react';
import { Routes, Route, Navigate, useNavigate, useParams } from 'react-router-dom';
import portalAPI from './portalApi';
import PortalAutopay from './PortalAutopay';
import PortalPayouts from './PortalPayouts';
import { PortalShell, usePortalBrand } from './PortalChrome';
import PortalCashout from './PortalCashout';
import { money, day, statusLabel, balanceLabel } from './format';
import { formatApiError } from '../../utils/errorMessage';

const errText = (e, fallback) => formatApiError(e, fallback);
const box = 'max-w-sm mx-auto mt-8 p-6 border rounded space-y-3';
const input = 'block w-full border rounded px-2 py-1';

// { slug, base, brand, missing } for the current portal address.
const PortalCtx = createContext({ slug: null, base: '/portal', brand: null, missing: false });
const usePortal = () => useContext(PortalCtx);

function NotFound() {
  return <div className={box}><p>This portal link was not found. Check the link you were sent, or use the <a className="underline" href="/portal/login">client sign-in</a>.</p></div>;
}

export function PortalLogin() {
  const nav = useNavigate();
  const { slug, base, brand, missing } = usePortal();
  const [email, setEmail] = useState(''); const [password, setPassword] = useState('');
  const [choices, setChoices] = useState(null); const [error, setError] = useState('');
  const submit = async (businessId) => {
    setError('');
    try { await portalAPI.login(email, password, businessId, slug && brand ? slug : undefined); nav(base, { replace: true }); } catch (e) {
      if (e.response && e.response.status === 409) setChoices(e.response.data.businesses); else setError(errText(e, 'Could not sign in'));
    }
  };
  if (missing) return <PortalShell brand={null}><NotFound /></PortalShell>;
  return (
    <PortalShell brand={brand}>
      <form className={box} onSubmit={(e) => { e.preventDefault(); submit(); }}>
        <h1 className="text-lg font-semibold">Client sign-in</h1>
        <input type="email" required aria-label="Email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} className={input} />
        <input type="password" required aria-label="Password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} className={input} />
        {choices ? choices.map((c) => <button type="button" key={c.business_id} className="block w-full border rounded py-1" onClick={() => submit(c.business_id)}>{c.business_name}</button>)
          : <button type="submit" className="w-full border rounded py-1">Sign in</button>}
        {error && <p role="alert" className="text-red-600 text-sm">{error}</p>}
      </form>
    </PortalShell>
  );
}

export function PortalAccept() {
  const nav = useNavigate();
  const { base, brand, missing } = usePortal();
  const token = new URLSearchParams(String(window.location.hash || '').replace(/^#/, '')).get('token') || '';
  const [password, setPassword] = useState(''); const [error, setError] = useState('');
  const submit = async () => {
    setError('');
    try { await portalAPI.acceptInvite(token, password); window.history.replaceState(null, '', base); nav(base, { replace: true }); } catch (e) { setError(errText(e, 'Could not accept the invite')); }
  };
  let content;
  if (missing) content = <NotFound />;
  else if (!token) content = <div className={box}><p>This invite link is not complete. Open the full link you were sent.</p></div>;
  else content = (
    <form className={box} onSubmit={(e) => { e.preventDefault(); submit(); }}>
      <h1 className="text-lg font-semibold">Set your password</h1>
      <input type="password" required minLength={10} aria-label="New password" placeholder="At least 10 characters" value={password} onChange={(e) => setPassword(e.target.value)} className={input} />
      <button type="submit" className="w-full border rounded py-1">Open my portal</button>
      {error && <p role="alert" className="text-red-600 text-sm">{error}</p>}
    </form>
  );
  return <PortalShell brand={missing ? null : brand}>{content}</PortalShell>;
}

export function PortalHome() {
  const nav = useNavigate();
  const { slug, base, brand } = usePortal();
  const [data, setData] = useState(null); const [error, setError] = useState('');
  useEffect(() => {
    let live = true;
    Promise.all([portalAPI.me(), portalAPI.invoices(), portalAPI.payments(), portalAPI.balance()])
      .then(([me, inv, pay, bal]) => { if (live) setData({ client: me.data.client, cardPayments: me.data.card_payments === true, invoices: inv.data.invoices, payments: pay.data.payments, balance: bal.data }); })
      .catch((e) => { if (!live) return; if (e.response && e.response.status === 401) nav(`${base}/login`, { replace: true }); else setError(errText(e, 'Could not load your portal')); });
    return () => { live = false; };
  }, [nav, base]);
  // On a plain /portal link the business is known once signed in.
  const ownSlug = data && data.client.business_slug;
  const { brand: lateBrand } = usePortalBrand(!slug && ownSlug ? ownSlug : null);
  const shownBrand = brand || lateBrand || (data ? { name: data.client.business_name } : null);
  if (error) return <PortalShell brand={shownBrand}><p role="alert" className="text-red-600">{error}</p></PortalShell>;
  if (!data) return <PortalShell brand={brand}><p>Loading...</p></PortalShell>;
  const pay = async (id) => {
    setError('');
    try { const r = await portalAPI.pay(id); window.location.assign(r.data.url); } catch (e) { setError(errText(e, 'Could not start the payment')); }
  };
  const signOut = async () => { try { await portalAPI.logout(); } finally { nav(`${base}/login`, { replace: true }); } };
  return (
    <PortalShell brand={shownBrand} right={<button type="button" onClick={signOut} className="text-sm underline">Sign out</button>}>
      <main className="space-y-6">
        {data.balance && (
          <section aria-label="Account balance">
            <h2 className="font-semibold mb-1">Account balance</h2>
            <p data-testid="portal-balance">{balanceLabel(data.balance.balance)}</p>
            {data.balance.entries.length > 0 && (
              <div className="overflow-x-auto"><table className="w-full text-sm mt-2"><thead><tr><th className="text-left">Date</th><th className="text-left">Note</th><th className="text-right">Amount</th></tr></thead>
                <tbody>{data.balance.entries.map((e) => <tr key={e.id}><td>{day(e.entry_date)}</td><td>{e.memo}</td><td className="text-right">{money(e.amount)}</td></tr>)}</tbody></table></div>
            )}
          </section>
        )}
        <section aria-label="Invoices">
          <h2 className="font-semibold mb-2">Invoices</h2>
          {data.invoices.length === 0 ? <p className="text-sm text-gray-600">No invoices yet.</p> : (
            <div className="overflow-x-auto"><table className="w-full text-sm"><thead><tr><th className="text-left">Invoice</th><th className="text-left">Date</th><th className="text-left">Due</th><th className="text-right">Amount</th><th className="text-right">Still owed</th><th className="text-left">Status</th><th /></tr></thead>
              <tbody>{data.invoices.map((i) => <tr key={i.id}><td>{i.invoice_number || i.id}</td><td>{day(i.invoice_date)}</td><td>{day(i.due_date)}</td><td className="text-right">{money(i.amount)}</td><td className="text-right">{money(i.amount_remaining)}</td><td>{statusLabel(i.display_status || i.status)}</td><td>{data.cardPayments && Number(i.amount_remaining) > 0 && !['paid', 'written_off', 'disputed', 'draft'].includes(i.display_status || i.status) && <button type="button" className="underline" onClick={() => pay(i.id)}>Pay {money(i.amount_remaining)}</button>}</td></tr>)}</tbody></table></div>
          )}
        </section>
        <PortalAutopay />
        <PortalPayouts />
        <PortalCashout />
        <section aria-label="Payments">
          <h2 className="font-semibold mb-2">Payment history</h2>
          {data.payments.length === 0 ? <p className="text-sm text-gray-600">No payments yet.</p> : (
            <div className="overflow-x-auto"><table className="w-full text-sm"><thead><tr><th className="text-left">Date</th><th className="text-left">Invoice</th><th className="text-right">Amount</th></tr></thead>
              <tbody>{data.payments.map((p) => <tr key={p.id}><td>{day(p.payment_date)}</td><td>{p.invoice_number || p.invoice_id}</td><td className="text-right">{money(p.amount)}</td></tr>)}</tbody></table></div>
          )}
        </section>
      </main>
    </PortalShell>
  );
}

export default function PortalApp() {
  const { slug: raw } = useParams();
  const slug = raw ? String(raw).toLowerCase() : null;
  const base = slug ? `/p/${slug}` : '/portal';
  const { brand, missing } = usePortalBrand(slug);
  return (
    <PortalCtx.Provider value={{ slug, base, brand, missing }}>
      <Routes>
        <Route path="login" element={<PortalLogin />} />
        <Route path="accept" element={<PortalAccept />} />
        <Route index element={<PortalHome />} />
        <Route path="*" element={<Navigate to={base} replace />} />
      </Routes>
    </PortalCtx.Provider>
  );
}
